import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppSettings } from 'app/modules/shared/app.settings';
import type { Country } from 'app/modules/shared/models/country';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-group-select',
  standalone: true,
  imports: [AutoCompleteModule, FormsModule, ReactiveFormsModule, TranslateModule, InputTextModule, DividerModule, CommonModule, RadioButtonModule],
  templateUrl: './group-select.component.html',
  styleUrl: './group-select.component.scss'
})
export class GroupSelectComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('auto') autoComplete!: AutoComplete;
  checked = [];
  @Input() attributeValues;
  @Input() field: any;
  @Input() attributeLabels: [];
  @Input() moduleName: string;
  @Output() onSearchValueChange: EventEmitter<string> = new EventEmitter();
  formName: FormGroup;
  searchValue: string = '';
  selectedControl: string[] = [];
  inputPlaceholder: string;
  filteredValues: any[] = [];
  suggestions: any[] | undefined;
  selectedItems: any = [];
  entityIds = [];
  items: any[] | undefined;
  country: Country;
  private isFirstTime: boolean = true;
  private subscription: Subscription;
  private clearSubscription: Subscription;
  moduleNameForConcerns;
  selectedCategory: any;
  categories: any;
  placeholder: any;
  entityObjSub: Subscription;
  userParentId: any;
  shouldTriggerSearch = false; 

  constructor(private formGroupDirective: FormGroupDirective,
    public cs: CommonBindingDataService,
    private entityService: EntityService,
    private configService: ConfigService,
    private route: ActivatedRoute) {
    this.formName = this.formGroupDirective.control;
  }


  ngOnInit(): void {
    this.selectedCategory = null;
    this.setRadioBtnView();
    if (this.formName.value.protocol_user_type) {
      this.entityObjSub = this.cs.entityObj$.subscribe((event: any) => {
        this.selectedCategory = event.type;
        this.userParentId = event.id;
        this.entityObjSub.unsubscribe();
      })
      this.onGroupTypeChange(this.selectedCategory, true);
      if (this.selectedCategory === AppSettings.ENTITY_CODE.SITE) {
        this.fetchFromAPI('', '', this.userParentId)
      }
    }
    this.placeholder = this.cs.getLabelValue(`${this.moduleName}.fields.${this.field.attributeCode}.placeholder`, this.attributeLabels, '')
  }

  ngAfterViewInit(): void {
    this.clearSubscription = this.cs.clearAutoComplete$.subscribe(() => {
      this.clearCompleteChange(this.autoComplete);
    });
  }

  setRadioBtnView() {
    this.categories = this.field.presetValues.map(ele =>
    (
      { name: ele.labelValue, key: ele.labelKey }
    )
    )
  }

  clearAutoComplete() {
    this.selectedItems = [];
    this.formName.controls.incident_complaint_date.setValue(null);
    this.formName.controls.journey_id.setValue(null);
    this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue('');
    this.formName.get(this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode).updateValueAndValidity();
  }

  clearCompleteChange(auto) {
    this.selectedItems = [];
    this.items = [];
    this.formName.controls[this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode].setValue('');
    this.formName.get(this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode).updateValueAndValidity();
    if (auto) {
        setTimeout(() => {
          auto.search('');
        }, 0);
      }

    this.shouldTriggerSearch = true; 
  }
  
  handleAutoFocus() {
    const inputValue = this.autoComplete.inputEL.nativeElement.value;
    if (!inputValue && !this.autoComplete.disabled) {
      setTimeout(() => {
        this.autoComplete.search('','','');
        this.autoComplete.show();
      }, 0);
    }
  }

  search(query) {
    if (query.length > 0) {
      this.formName.controls[this.field.attributeCode].markAsTouched();
      this.formName.controls[this.field.attributeCode].markAsDirty();
    }
    this.fetchFromAPI(query, '');
  }

  onSelectItem(event) {
    this.entityIds.push(event.value.id)
    this.formName.controls[this.field.attributeCode].setValue(this.entityIds);
    this.formName.controls[this.field.attributeCode].updateValueAndValidity();
  }


  fetchFromAPI(search: string, actionStatus: string, onEditId?) {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL_SITE, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW, false).subscribe(res => {
      if (res) {
        let protocolId;
        const protocolAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'protocol_entity_id')?.attributeId;
        const siteStatus = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'site_status');
        const siteAttributeId = siteStatus?.attributeId;
        const presetVal = siteStatus.presetValues?.find(p => p.labelKey === 'Active');
        const acceptPreset = presetVal?.labelKey;
        this.route.queryParams.subscribe(param => {
          protocolId = param?.id
        });
        const requestBody = {
          viewCode: AppSettings.VIEW_CODE.AUTOCOMPLETE_VIEW,
          countryCode: this.country[0].countryCode,
          forTenantCode: this.configService.getForTenantCode(),
          searchText: search,
          actionStatus,
          deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
          filters: [{ attributeId: protocolAttributeId, attributeValue: protocolId },
            { attributeId: siteAttributeId, attributeValue: [acceptPreset]}
          ]
        }
        this.entityService.autoComplete(AppSettings.ENTITY_CODE.PROTOCOL_SITE, requestBody).subscribe(res => {
          this.items = this.cs.getTableData(res) || [];
          this.setSiteOptions(this.items, onEditId);
        });
      }
    });
  }

  setSiteOptions(items: any, onEditId) {
    this.items = [];
    items.forEach(ele => {
      this.items.push({ label: ele.site_name, id: ele.site_entity_id })
    })
    if (onEditId) {
      this.selectedItems = this.items.filter(ele => ele.id === onEditId)[0];
      this.cs.emitEntityObj({ id: onEditId, type: 'site' });
    }
  }

  onSelectSingleItem(event) {
    const entityObj = { id: event.value.id, type: this.selectedCategory };
    this.entityIds = event.value.id;
    this.formName.controls[this.field.attributeCode].setValue(this.entityIds);
    this.formName.controls[this.field.attributeCode].updateValueAndValidity();
    this.cs.emitEntityObj(entityObj);
  }

  handleSingleUnselect() {
    this.entityIds = [];
    this.formName.controls[this.field.attributeCode].setValue(null);
    this.formName.controls[this.field.attributeCode].updateValueAndValidity();
  }

  ngOnDestroy() {
    this.cs.emitEntityObj({ id: null, type: null });
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.clearSubscription) {
      this.clearSubscription.unsubscribe();
    }
    if (this.entityObjSub) {
      this.entityObjSub.unsubscribe();
    }
  }

  onGroupTypeChange(event, isEdit?) {
    if (event === AppSettings.ENTITY_CODE.SPONSOR) {
      this.items = [];
      this.selectedItems = [];
      const sponsorData = this.entityService.getRelateData;
      this.selectedItems.push(sponsorData.sponsor_name);
      this.entityIds = sponsorData.entityId;
      this.formName.controls[this.field.attributeCode].setValue(this.entityIds);
      this.formName.controls[this.field.attributeCode].updateValueAndValidity();
      const entityObj = { id: this.entityIds, type: this.selectedCategory }
      this.cs.emitEntityObj(entityObj);

    } else if (event === AppSettings.ENTITY_CODE.SITE) {
      this.cs.emitEntityObj({ id: null, type: 'site' });
      this.items = [];
      this.selectedItems = [];
      if(!isEdit) {
        this.autoComplete.search('','','');
      }
      if(isEdit) {
        this.formName.controls[this.field.attributeCode].setValue(this.userParentId);
        this.formName.controls[this.field.attributeCode].updateValueAndValidity();
      }
    }
  }

}


